import React, { useMemo } from "react";
import { compareAsc, parseISO } from "date-fns";
import { DriveRow } from "./components/driveRow";
import HomeRow from "./homeRow";
import { type UploadStatusMessage, useUploadStatus } from "@/hooks/use-upload-status";
import { FolderItem, Item } from "@/types/fse";
import { TxType } from "@/types/transaction";

interface DriveListProps {
  items: Item[];
  parentFolder: FolderItem | null;
  isRootFolder: boolean;
  currentPage: number;
  itemsPerPage: number;
  sortItemsByDate?: boolean;
  hiddenButtons?: Array<string>;
}

const _sortByDate = (a: Item, b: Item) => compareAsc(parseISO(b.created_at), parseISO(a.created_at));

export const DriveList: React.FC<DriveListProps> = ({
  items,
  parentFolder,
  isRootFolder,
  currentPage,
  itemsPerPage,
  sortItemsByDate = true,
  hiddenButtons = [],
}) => {
  const [progressForItem, setProgressForItem] = React.useState<Record<string, [number, TxType]>>({});

  const { paginatedItems } = useMemo(() => {
    const folders = items.filter(item => item.type === "folder");
    const files = items.filter(item => item.type === "file");

    const sortedFolders = sortItemsByDate ? folders.sort(_sortByDate) : folders;
    const sortedFiles = sortItemsByDate ? files.sort(_sortByDate) : files;

    const allSortedItems = [...sortedFolders, ...sortedFiles];

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedItems = allSortedItems.slice(startIndex, endIndex);

    return { paginatedItems };
  }, [items, currentPage, itemsPerPage, sortItemsByDate]);

  const onUploadStatusChange = React.useCallback((event: UploadStatusMessage) => {
    if (event.type === "document" && [TxType.UPLOAD, TxType.CHRONOLOGY].includes(event.tx_type)) {
      setProgressForItem(prev => {
        event.fse_ids.forEach(fseId => {
          prev[fseId] = [event.progress, event.tx_type];
        });
        return prev;
      });
    }
  }, []);

  useUploadStatus(onUploadStatusChange);

  return (
    <div className="divide-y divide-gray-200 rounded-lg bg-white">
      {!isRootFolder && parentFolder ? <HomeRow parentFolder={parentFolder} /> : null}
      {paginatedItems.map(item => (
        <DriveRow
          parentFolder={parentFolder}
          key={item.id}
          item={item}
          progressForItem={progressForItem}
          hiddenButtons={hiddenButtons}
        />
      ))}
    </div>
  );
};
